import React from "react";
import { Card, Col, Row, Statistic, Select, Table, Tag, Typography, Button } from "antd";
import {
    UserAddOutlined,
    RiseOutlined,
    ThunderboltOutlined,
    PhoneOutlined,
    SendOutlined,
    LineChartOutlined,
    ClockCircleOutlined,
    DollarCircleOutlined,
    PieChartOutlined,
    FunnelPlotOutlined,
    BarChartOutlined,
    FileExcelOutlined,
    FilePdfOutlined
} from "@ant-design/icons";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Legend,
    LineChart,
    Line
} from "recharts";

const { Option } = Select;
const { Title } = Typography;

const pieData = [
    { name: "Website", value: 400 },
    { name: "LinkedIn", value: 300 },
    { name: "Referral", value: 300 },
    { name: "Ads", value: 200 }
];

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const funnelData = [
    { stage: "Captured", count: 950 },
    { stage: "Contacted", count: 780 },
    { stage: "Qualified", count: 310 },
    { stage: "Proposal", count: 105 },
    { stage: "Converted", count: 58 }
];

const trendData = [
    { week: "Week 1", leads: 120 },
    { week: "Week 2", leads: 150 },
    { week: "Week 3", leads: 100 },
    { week: "Week 4", leads: 190 }
];

const goalData = [
    { developer: "John", target: 30, achieved: 25 },
    { developer: "Sophie", target: 40, achieved: 35 },
    { developer: "Mark", target: 50, achieved: 48 }
];

const businessUnits = ["All Units", "Fix Bid Projects", "Product", "Contract Deployment"];
const developers = ["All Developers", "John", "Sophie", "Mark"];

const mockLeadDetails = [
    {
        key: "1",
        name: "John Smith",
        businessUnit: "Fix Bid Projects",
        status: "Qualified",
        value: "$12,000",
        owner: "Sophie",
        source: "Website"
    },
    {
        key: "2",
        name: "ACME Corp",
        businessUnit: "Contract Deployment",
        status: "Proposal",
        value: "$25,000",
        owner: "Mark",
        source: "Referral"
    },
    {
        key: "3",
        name: "NextGen Tools",
        businessUnit: "Product",
        status: "Converted",
        value: "$5,000",
        owner: "John",
        source: "LinkedIn"
    }
];

const columns = [
    { title: "Lead Name", dataIndex: "name", key: "name" },
    { title: "Business Unit", dataIndex: "businessUnit", key: "businessUnit" },
    { title: "Status", dataIndex: "status", key: "status", render: (status: string) => <Tag color="blue">{status}</Tag> },
    { title: "Value", dataIndex: "value", key: "value" },
    { title: "Owner", dataIndex: "owner", key: "owner" },
    { title: "Source", dataIndex: "source", key: "source" }
];

const LeadDashboard: React.FC = () => {
    const [selectedBU, setSelectedBU] = React.useState("All Units");
    const [selectedDev, setSelectedDev] = React.useState("All Developers");
    const [isAdmin, setIsAdmin] = React.useState(true);

    const handleBUChange = (value: string) => setSelectedBU(value);
    const handleDevChange = (value: string) => setSelectedDev(value);

    return (
        <div className="p-6 space-y-6">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                <Title level={4}>Lead Dashboard ({selectedBU} - {selectedDev})</Title>
                <div className="flex gap-2">
                    <Select defaultValue={selectedBU} onChange={handleBUChange} style={{ width: 200 }}>
                        {businessUnits.map(unit => <Option key={unit} value={unit}>{unit}</Option>)}
                    </Select>
                    <Select defaultValue={selectedDev} onChange={handleDevChange} style={{ width: 200 }}>
                        {developers.map(dev => <Option key={dev} value={dev}>{dev}</Option>)}
                    </Select>
                </div>
            </div>

            <Row gutter={[16, 16]}>
                {[950, 120, 310, 230, 105, 58].map((value, index) => (
                    <Col xs={24} sm={12} md={8} lg={4} key={index}>
                        <Card>
                            <Statistic
                                title={["Total Leads", "New Leads", "Qualified", "Contacted", "Proposals", "Converted"][index]}
                                value={value}
                                prefix={[<UserAddOutlined />, <RiseOutlined />, <ThunderboltOutlined />, <PhoneOutlined />, <SendOutlined />, <LineChartOutlined />][index]}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                    <Card>
                        <Statistic title="Conversion Rate" value={6.1} precision={1} suffix="%" valueStyle={{ color: "#3f8600" }} />
                    </Card>
                </Col>
                <Col xs={24} md={8}>
                    <Card>
                        <Statistic title="Avg. Response Time" value="2h 45m" prefix={<ClockCircleOutlined />} valueStyle={{ color: "#faad14" }} />
                    </Card>
                </Col>
                <Col xs={24} md={8}>
                    <Card>
                        <Statistic title="Avg. Deal Size" value={8500} prefix={<DollarCircleOutlined />} precision={0} valueStyle={{ color: "#1890ff" }} suffix="$" />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} xl={12}>
                    <Card title="Lead Source Breakdown" extra={<PieChartOutlined />}>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} fill="#8884d8" label>
                                    {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
                <Col xs={24} xl={12}>
                    <Card title="Lead Funnel Overview" extra={<FunnelPlotOutlined />}>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={funnelData} layout="vertical">
                                <XAxis type="number" hide />
                                <YAxis type="category" dataKey="stage" width={100} />
                                <Bar dataKey="count" fill="#1890ff" />
                                <Tooltip />
                                <Legend />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card title="Leads Trend (Weekly)" extra={<BarChartOutlined />}>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={trendData}>
                                <XAxis dataKey="week" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="leads" stroke="#8884d8" />
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
            </Row>

            {isAdmin && (
                <>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Card title="Business Developer Target Report">
                                <Table
                                    columns={[
                                        { title: "Developer", dataIndex: "developer", key: "developer" },
                                        { title: "Target", dataIndex: "target", key: "target" },
                                        { title: "Achieved", dataIndex: "achieved", key: "achieved" }
                                    ]}
                                    dataSource={goalData.map((item, i) => ({ key: i, ...item }))}
                                    pagination={false}
                                />
                            </Card>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col span={24} className="flex justify-end gap-2">
                            <Button icon={<FileExcelOutlined />} type="default">
                                Export to Excel
                            </Button>
                            <Button icon={<FilePdfOutlined />} type="default">
                                Export to PDF
                            </Button>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Card title="Lead Details">
                                <Table columns={columns} dataSource={mockLeadDetails} pagination={{ pageSize: 5 }} />
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
};

export default LeadDashboard;